import React, { useState, useEffect } from "react";
import { Page } from "components/shared/Page";
import { Card } from "components/ui";
import sunnyHomesCrmLogo from "assets/images/SH-logo.png";
import constructionBg from "assets/images/construction_bg.png";
import { JWT_HOST_API } from "configs/auth.config";
import WhsSideMenu from "../../whs-qr/WhsSideMenu";

export default function WhsPageLayout({
  title,
  subtitle = "Site Safety & Check-in System",
  pageHeaderTitle = title,
  currentPage,
  token,
  jobData,
  userForm,
  workerStatus,
  activeCheckInDate,
  onCheckOutClick,
  showSideMenu = false,
  companyLogo: propCompanyLogo,
  maxWidth = "sm:max-w-2xl",
  children,
  modals = null,
}) {
  const [resolvedCompanyLogo, setResolvedCompanyLogo] = useState(
    propCompanyLogo || sunnyHomesCrmLogo
  );

  useEffect(() => {
    if (propCompanyLogo) {
      setResolvedCompanyLogo(propCompanyLogo);
      return;
    }
    try {
      const hostname = window.location.hostname;
      let folderName = hostname;
      if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
        folderName = "localhost";
      }
      const apiHost = JWT_HOST_API?.endsWith("/") ? JWT_HOST_API : `${JWT_HOST_API}/`;
      setResolvedCompanyLogo(`${apiHost}CompanyLogos/${folderName}/main.png`);
    } catch (err) {
      console.error("Failed to set company logo from URL:", err);
    }
  }, [propCompanyLogo]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("whs_sidebar_open");
      if (stored !== null) return stored === "true";
      return window.innerWidth >= 1024;
    }
    return false;
  });

  const handleSidebarToggle = (nextVal) => {
    setIsSidebarOpen((prev) => {
      const resolved = typeof nextVal === "function" ? nextVal(prev) : nextVal;
      try {
        localStorage.setItem("whs_sidebar_open", resolved ? "true" : "false");
      } catch (err) {
        console.warn("Storage warning:", err);
      }
      return resolved;
    });
  };

  return (
    <Page title={title}>
      {showSideMenu && (
        <WhsSideMenu
          token={token}
          jobData={jobData}
          userForm={userForm}
          workerStatus={workerStatus}
          activeCheckInDate={activeCheckInDate}
          onCheckOutClick={onCheckOutClick}
          currentPage={currentPage}
          companyLogo={resolvedCompanyLogo}
          isOpen={isSidebarOpen}
          onToggle={handleSidebarToggle}
        />
      )}

      <div
        className={`relative flex flex-col w-full min-h-[100dvh] justify-start sm:justify-center items-center bg-slate-50 px-2.5 sm:px-4 md:px-6 pt-[max(1.5rem,calc(env(safe-area-inset-top,0px)+1.25rem))] pb-[max(1.5rem,calc(env(safe-area-inset-bottom,0px)+1.5rem))] overflow-y-auto overflow-x-hidden selection:bg-primary-500/30 selection:text-slate-900 font-sans ${
          showSideMenu && isSidebarOpen ? "lg:pl-[350px]" : ""
        } transition-all duration-300`}
      >
        {/* Background construction image + overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-700 opacity-60"
          style={{ backgroundImage: `url(${constructionBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-50/90 via-slate-100/80 to-primary-100/30 backdrop-blur-[5px]" />

        {/* Ambient background glows */}
        <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-primary-300/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-80 h-80 rounded-full bg-amber-300/15 blur-3xl pointer-events-none" />

        <div
          className={`relative z-10 w-full max-w-full ${maxWidth} my-auto sm:my-3 animate-in fade-in duration-200 overflow-x-hidden`}
        >
          {/* Header Branding Container */}
          <div className="mb-3 text-center">
            <div className="mb-2 flex items-center justify-center space-x-2 rounded-2xl border border-white/80 bg-white/95 px-3.5 py-1.5 mx-auto w-fit backdrop-blur-md shadow-xs">
              <img
                src={resolvedCompanyLogo}
                className="h-7 object-contain"
                alt="logo"
                onError={(e) => {
                  e.target.src = sunnyHomesCrmLogo;
                }}
              />
            </div>

            <div className="flex flex-col items-center gap-0.5">
              <h1 className="text-base font-bold text-slate-900 tracking-tight">
                {pageHeaderTitle}
              </h1>
              {subtitle && (
                <p className="text-[11px] font-semibold text-slate-500">{subtitle}</p>
              )}
            </div>
          </div>

          <Card className="bg-white/95 px-3.5 py-4 sm:p-5 md:p-6 backdrop-blur-2xl rounded-2xl sm:rounded-3xl border border-white/60 relative overflow-hidden shadow-none">
            {children}
          </Card>
        </div>
      </div>

      {modals}
    </Page>
  );
}
